import { FilterItem, FilterItemOperator, SortItem } from "namirasoft-core";
import
{
    AccountRow, CurrencyRow, NamirasoftERPMetaDatabase,
    PartyRow, TagRow
} from "namirasoft-erp";
import
{
    NSBox,
    NSBoxEntity, NSBoxMoney, NSLabel
} from "namirasoft-site-react";
import { Component, createRef } from "react";
import { ErpRouterProps } from "../ErpRouterProps";
import { ErpScope } from "../ErpScope";

export interface JournalEntryLineInput
{
    account_id: string;
    party_id: string | null;
    tag_ids: string[];
    currency_id: string;
    debit: number;
    credit: number;
}

interface LineItemProps
{
    servers: ErpRouterProps["servers"];
    db: NamirasoftERPMetaDatabase;
    companyId: string | null;
    branchId: string | null;
    multiCurrency: boolean;
    entryCurrencyId: string | null;
    onChange: () => void;
    onDelete?: (lIndex: number) => void;
    lIndex: number;
}

interface LineItemState
{
    allowedPartyTypeIds: string[] | null;
    allowedTagGroupIds: string[] | null;
}

export class JournalEntryLineItem extends Component<LineItemProps, LineItemState>
{
    override state: LineItemState = { allowedPartyTypeIds: null, allowedTagGroupIds: null };

    private isLoading = false;
    // Set true when the user changes the account (so the next Party load should try
    // to auto-pick a single match), false when setValue is loading a saved row (so
    // the saved party_id wins and is not overridden by the auto-select).
    private shouldAutoSelectParty = false;

    Account = createRef<NSBoxEntity<AccountRow>>();
    Party = createRef<NSBoxEntity<PartyRow>>();
    Tags = createRef<NSBoxEntity<TagRow>>();
    Debit = createRef<NSBoxMoney>();
    Credit = createRef<NSBoxMoney>();
    Currency = createRef<NSBoxEntity<CurrencyRow>>();

    private applyConstraintsFromAccount = (callback?: () => void): void =>
    {
        let account = this.Account.current?.getDataOne(false) ?? null;
        let allowedPartyTypeIds: string[] | null = account ? (account.party_type_ids ?? []) : null;
        let allowedTagGroupIds: string[] | null = account ? (account.tag_group_ids ?? []) : null;
        this.setState({ allowedPartyTypeIds, allowedTagGroupIds }, () =>
        {
            callback?.();
        });
    };
    private tryAutoSelectSingleParty = (): void =>
    {
        if (!this.shouldAutoSelectParty)
            return;
        this.shouldAutoSelectParty = false;
        if (this.isLoading)
            return;
        if (this.Party.current?.getValueOne(false))
            return;

        let items = this.Party.current?.NSBoxBaseCombo?.current?.getRows() ?? [];
        if (items.length !== 1)
            return;
        this.isLoading = true;
        this.Party.current?.setValueOne((items[0] as PartyRow).id, () =>
        {
            this.isLoading = false;
            this.props.onChange();
        });
    };

    private onAccountChanged = (): void =>
    {
        if (this.isLoading)
            return;
        this.isLoading = true;
        this.Tags.current?.setValueAll([]);
        let proceed = () =>
        {
            this.isLoading = false;
            this.shouldAutoSelectParty = true;
            this.applyConstraintsFromAccount();
            this.props.onChange();
        };
        if (this.Party.current)
            this.Party.current.setValueOne(null, proceed);
        else
            proceed();
    };

    private onPartyChanged = (): void =>
    {
        if (this.isLoading)
            return;
        this.props.onChange();
    };

    private onTagsChanged = (): void =>
    {
        if (this.isLoading)
            return;
        this.props.onChange();
    };

    getValue(checkError: boolean = true): JournalEntryLineInput | null
    {
        let account_id = this.Account.current?.getValueOne(checkError) ?? "";
        let currency_id = this.props.multiCurrency
            ? (this.Currency.current?.getValueOne(checkError) ?? "")
            : (this.props.entryCurrencyId ?? "");
        return {
            account_id,
            party_id: this.Party.current?.getValueOne(false) ?? null,
            tag_ids: this.Tags.current?.getValueAll() ?? [],
            currency_id,
            debit: Number(this.Debit.current?.getValue(false) ?? 0),
            credit: Number(this.Credit.current?.getValue(false) ?? 0),
        };
    }

    setValue(value: JournalEntryLineInput | null): void
    {
        this.isLoading = true;
        this.Debit.current?.setValue(value?.debit ?? 0, () => this.syncDisabled());
        this.Credit.current?.setValue(value?.credit ?? 0, () => this.syncDisabled());
        this.syncDisabled();
        let accountId = value?.account_id ?? null;
        let partyId = value?.party_id ?? null;
        let tagIds = value?.tag_ids ?? [];
        if (this.props.multiCurrency)
            this.Currency.current?.setValueOne(value?.currency_id ?? null);
        this.Account.current?.setValueOne(accountId, () =>
        {
            this.applyConstraintsFromAccount(() =>
            {
                this.Party.current?.setValueOne(partyId);
                this.Tags.current?.setValueAll(tagIds);
                this.isLoading = false;
                this.props.onChange();
            });
        });
    }

    isEmpty(): boolean
    {
        return !this.Account.current?.getValueOne(false)
            && !this.Debit.current?.getValue()
            && !this.Credit.current?.getValue();
    }

    private syncDisabled(): void
    {
        let debit = Number(this.Debit.current?.getValue() ?? 0);
        let credit = Number(this.Credit.current?.getValue() ?? 0);
        this.Debit.current?.setDisabled(credit > 0);
        this.Credit.current?.setDisabled(debit > 0);
    }

    private onDebitChanged = (): void =>
    {
        if (this.isLoading)
            return;
        this.syncDisabled();
        this.props.onChange();
    };

    private onCreditChanged = (): void =>
    {
        if (this.isLoading)
            return;
        this.syncDisabled();
        this.props.onChange();
    };

    private withScope<T>(
        table: { columns: { company_id: any; branch_id: any; enabled: any } },
        list: (filters: FilterItem[] | null, page: number | null, size: number | null, sorts: SortItem[], all_workspaces: boolean | null) => Promise<{ count: number; rows: T[] }>,
    ): (filters: FilterItem[] | null, page: number | null, size: number | null, sorts: SortItem[]) => Promise<{ count: number; rows: T[] }>
    {
        return (filters, page, size, sorts) =>
        {
            let companyId = ErpScope.getCompanyIdRaw();
            let branchId = ErpScope.getBranchIdRaw();
            // Without a proper scope value there is nothing to scope by, so do not load.
            if (!companyId || !branchId)
                return Promise.resolve({ count: 0, rows: [] });
            let scoped = ErpScope.pushFilter(filters, table, "company_id", companyId);
            scoped = ErpScope.pushFilter(scoped, table, "branch_id", branchId);
            scoped.push(new FilterItem(table as any, table.columns.enabled, false, FilterItemOperator.all.equals, "1"));
            return list(scoped, page, size, sorts, null);
        };
    }

    override render()
    {
        let { servers, db } = this.props;
        let hasAccount = !!this.Account.current?.getValueOne(false);
        let showParty = hasAccount && this.state.allowedPartyTypeIds !== null && this.state.allowedPartyTypeIds.length > 0;
        let showTags = hasAccount && this.state.allowedTagGroupIds !== null && this.state.allowedTagGroupIds.length > 0;
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "nowrap", width: "100%" }}>
                <NSLabel
                    title=""
                    style={{ width: 28, flexShrink: 0 }}
                >
                    {this.props.lIndex + 1}
                </NSLabel>
                <NSBoxEntity<AccountRow>
                    ref={this.Account} title="Account" info={{ link: "https://namirasoft.com/erp/console-guide/journal-entry/#field-line-account" }} required multiple={false}
                    table_name="account" table={db.tables.account}
                    list={(filters, page, size, sorts) =>
                    {
                        let f: FilterItem[] = filters ? [...filters] : [];
                        f.push(new FilterItem(db.tables.account as any, db.tables.account.columns.is_group, false, FilterItemOperator.all.equals, "0"));
                        let scoped = this.withScope<AccountRow>(db.tables.account, servers.erp.account.List);
                        return scoped(f, page, size, sorts);
                    }}
                    getValue={(item) => item.id} getTitle={(item) => `${item.code} - ${item.name}`} getSort={() => null}
                    style={{ flexGrow: 1, flexShrink: 1, flexBasis: 0, width: "auto", minWidth: 0 }}
                    hideHeader
                    placeholder="Account"
                    onChanged={this.onAccountChanged}
                />
                {showParty && (
                    <NSBoxEntity<PartyRow>
                        key={`party-${(this.state.allowedPartyTypeIds ?? []).join(",")}`}
                        ref={this.Party} title="Party" info={{ link: "https://namirasoft.com/erp/console-guide/journal-entry/#field-line-party" }} required={false} multiple={false}
                        table_name="party" table={db.tables.party}
                        list={async (filters, page, size, sorts) =>
                        {
                            let scoped = this.withScope<PartyRow>(db.tables.party, servers.erp.party.List);
                            let result = await scoped(filters, page, size, sorts);
                            let allowed = this.state.allowedPartyTypeIds;
                            if (allowed !== null && allowed.length > 0)
                            {
                                let allowedSet = new Set(allowed);
                                let rows = result.rows.filter(r => allowedSet.has(r.party_type_id));
                                return { count: rows.length, rows };
                            }
                            if (allowed !== null && allowed.length === 0)
                                return { count: 0, rows: [] };
                            return result;
                        }}
                        getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                        style={{ width: NSBox.width.one, flexShrink: 0 }}
                        hideHeader
                        placeholder="Party"
                        onChanged={this.onPartyChanged}
                        onLoaded={this.tryAutoSelectSingleParty}
                    />
                )}
                {showTags && (
                    <NSBoxEntity<TagRow>
                        key={`tags-${(this.state.allowedTagGroupIds ?? []).join(",")}`}
                        ref={this.Tags} title="Tags" info={{ link: "https://namirasoft.com/erp/console-guide/journal-entry/#field-line-tags" }} required={false} multiple={true}
                        table_name="tag" table={db.tables.tag}
                        list={async (filters, page, size, sorts) =>
                        {
                            let scoped = this.withScope<TagRow>(db.tables.tag, servers.erp.tag.List);
                            let result = await scoped(filters, page, size, sorts);
                            let allowed = this.state.allowedTagGroupIds;
                            if (allowed !== null && allowed.length > 0)
                            {
                                let allowedSet = new Set(allowed);
                                let rows = result.rows.filter(r => allowedSet.has(r.tag_group_id));
                                return { count: rows.length, rows };
                            }
                            if (allowed !== null && allowed.length === 0)
                                return { count: 0, rows: [] };
                            return result;
                        }}
                        getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                        style={{ width: NSBox.width.one, flexShrink: 0 }}
                        hideHeader
                        placeholder="Tags"
                        onChanged={this.onTagsChanged}
                    />
                )}
                <NSBoxMoney
                    ref={this.Debit} required={false} title="Debit" info={{ link: "https://namirasoft.com/erp/console-guide/journal-entry/#field-line-debit" }}
                    style={{ width: 160, flexShrink: 0 }}
                    hideHeader
                    placeholder="Debit"
                    onChanged={this.onDebitChanged}
                />
                <NSBoxMoney
                    ref={this.Credit} required={false} title="Credit" info={{ link: "https://namirasoft.com/erp/console-guide/journal-entry/#field-line-credit" }}
                    style={{ width: 160, flexShrink: 0 }}
                    hideHeader
                    placeholder="Credit"
                    onChanged={this.onCreditChanged}
                />
                {
                    this.props.multiCurrency && (
                        <NSBoxEntity<CurrencyRow>
                            ref={this.Currency} title="Currency" info={{ link: "https://namirasoft.com/erp/console-guide/journal-entry/#field-line-currency" }} required multiple={false}
                            table_name="currency" table={db.tables.currency}
                            list={(filters, page, size, sorts) =>
                            {
                                let f: FilterItem[] = filters ? [...filters] : [];
                                f.push(new FilterItem(db.tables.currency as any, db.tables.currency.columns.enabled, false, FilterItemOperator.all.equals, "1"));
                                return servers.erp.currency.List(f, page, size, sorts, null);
                            }}
                            getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                            style={{ width: 100, flexShrink: 0 }}
                            hideHeader
                            placeholder="Currency"
                            onChanged={() => { if (!this.isLoading) this.props.onChange(); }}
                        />
                    )
                }
                <img
                    src="https://static.namirasoft.com/image/concept/close/blue.svg"
                    alt="Remove"
                    title="Remove"
                    style={{
                        width: 20,
                        height: 20,
                        flexShrink: 0,
                        cursor: this.props.onDelete ? "pointer" : "default",
                        visibility: this.props.onDelete ? "visible" : "hidden",
                    }}
                    onClick={() => this.props.onDelete?.(this.props.lIndex)}
                />
            </div>
        );
    }
}